import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import { getEmptyCoupon } from '../../interfaces/Coupon/Coupon';
import {
  createCoupon,
  deleteCoupon,
  getCoupon,
  updateCoupon,
} from '../../api/Coupon';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';
import {
  CouponType,
  couponTypeNameMap,
} from '../../interfaces/Coupon/CouponType';
import NumberInput from '../../components/NumberInput/NumberInput';
import { ToggledNumberInput } from '../../components/NumberInput/ToggledNumberInput';
import { CategoriesDropdown } from '../../components/Dropdowns/CategoriesDropdown';
import { ProductsDropdown } from '../../components/Dropdowns/ProductsDropdown';
import { UsersDropdown } from '../../components/Dropdowns/UsersDropdown';

const minWidth = 400;
const maxWidth = 600;

export const ModifyCouponPage = () => {
  const navigate = useNavigate();

  const { couponID: couponIDAsStr } = useParams<string>();

  const couponID = couponIDAsStr ? Number(couponIDAsStr) : undefined;
  const isEditing = Number.isFinite(couponID);

  const [coupon, setCoupon] = useState(getEmptyCoupon());
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const refreshCoupon = () => {
    setLoading(true);
    getCoupon(couponID!)
      .then((c) => setCoupon(c))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshCoupon();
    }
  }, [couponIDAsStr]);

  const handleSave = () => {
    if (isEditing) {
      setLoading(true);
      updateCoupon(coupon)
        .then(() => {
          toast.success('Kupon başarıyla güncellendi');
          refreshCoupon();
        })
        .catch((err) => {
          toast.error(err.message);
          setLoading(false);
        });
    } else {
      setLoading(true);
      createCoupon(coupon)
        .then((createdCoupon) => {
          toast.success('Kupon başarıyla oluşturuldu');
          navigate(`/sales/coupons/${createdCoupon.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deleteCoupon(coupon.id)
      .then(() => {
        toast.success('Kupon başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/sales/coupons');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili kuponu silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}

      <Waiting open={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Kuponu Düzenle' : 'Yeni Kupon'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'fit-content',
          gap: 1.5,
          minWidth: 0,
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 3,
              flexGrow: 1,
              minWidth,
              maxWidth,
            }}
          >
            <Typography
              sx={{
                ...sharedStyles.h5,
                fontWeight: 'bold',
              }}
            >
              Genel
            </Typography>
            <Divider />

            <TextField
              label='İsim'
              variant='outlined'
              size='small'
              value={coupon.name}
              onChange={(e) => setCoupon({ ...coupon, name: e.target.value })}
              fullWidth
            />

            <TextField
              label='Kod'
              variant='outlined'
              size='small'
              value={coupon.code}
              onChange={(e) => setCoupon({ ...coupon, code: e.target.value })}
              fullWidth
            />

            <FormControl size='small' fullWidth>
              <InputLabel>İndirim Tipi</InputLabel>
              <Select
                label='İndirim Tipi'
                value={coupon.type}
                onChange={(e) =>
                  setCoupon({ ...coupon, type: e.target.value as CouponType })
                }
              >
                {Object.entries(couponTypeNameMap).map(([key, value]) => (
                  <MenuItem key={key} value={key}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <NumberInput
              label='İndirim'
              value={coupon.value}
              onChange={(newValue) => setCoupon({ ...coupon, value: newValue })}
              acceptFloat
              endAdornment={
                <Typography>
                  {coupon.type === CouponType.FIXED ? '₺' : '%'}
                </Typography>
              }
            />

            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DateTimePicker
                label='Başlangıç'
                onChange={(newStartAt) =>
                  setCoupon({ ...coupon, startAt: newStartAt ?? new Date() })
                }
                value={coupon.startAt}
                inputFormat='d MMMM yyyy HH:mm'
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </LocalizationProvider>

            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DateTimePicker
                label='Bitiş'
                onChange={(newEndAt) =>
                  setCoupon({ ...coupon, endAt: newEndAt ?? new Date() })
                }
                value={coupon.endAt}
                inputFormat='d MMMM yyyy HH:mm'
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </LocalizationProvider>

            <FormControlLabel
              control={
                <Checkbox
                  checked={coupon.isActive}
                  onChange={() =>
                    setCoupon({
                      ...coupon,
                      isActive: !coupon.isActive,
                    })
                  }
                />
              }
              label={<Typography sx={sharedStyles.subtitle2}>Aktif</Typography>}
            />
          </Paper>

          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 3,
              flexGrow: 1,
              minWidth,
              maxWidth,
            }}
          >
            <Typography
              sx={{
                ...sharedStyles.h5,
                fontWeight: 'bold',
              }}
            >
              Kullanım Koşulları
            </Typography>
            <Divider />

            <NumberInput
              label='Minimum Sipariş Tutarı'
              value={coupon.minimumSpend}
              onChange={(newValue) =>
                setCoupon({ ...coupon, minimumSpend: newValue })
              }
              acceptFloat
              endAdornment={<Typography>₺</Typography>}
            />

            <ToggledNumberInput
              label='Maksimum İndirim Tutarı'
              value={coupon.maximumDiscount}
              defaultValue={500}
              onChange={(newValue) =>
                setCoupon({ ...coupon, maximumDiscount: newValue })
              }
              acceptFloat
              endAdornment={<Typography>₺</Typography>}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {coupon.specificForUser ? (
                <UsersDropdown
                  label='Seçili Kullanıcılar'
                  userEmails={coupon.userEmails}
                  onChange={(newEmails) =>
                    setCoupon({ ...coupon, userEmails: newEmails })
                  }
                />
              ) : (
                <TextField
                  label='Seçili Kullanıcılar'
                  size='small'
                  value='Tüm Kullanıcılar'
                  disabled
                  fullWidth
                />
              )}

              <Switch
                checked={coupon.specificForUser}
                onChange={() =>
                  setCoupon({
                    ...coupon,
                    specificForUser: !coupon.specificForUser,
                  })
                }
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {coupon.specificForCategory ? (
                <CategoriesDropdown
                  label='Seçili Kategoriler'
                  categoryIDs={coupon.categoryIDs}
                  onChange={(newIDs) =>
                    setCoupon({ ...coupon, categoryIDs: newIDs })
                  }
                />
              ) : (
                <TextField
                  label='Seçili Kategoriler'
                  size='small'
                  value='Tüm kategoriler'
                  disabled
                  fullWidth
                />
              )}

              <Switch
                checked={coupon.specificForCategory}
                onChange={() =>
                  setCoupon({
                    ...coupon,
                    specificForCategory: !coupon.specificForCategory,
                    specificForProduct: false,
                    categoryIDs: [],
                    productIDs: [],
                  })
                }
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              {coupon.specificForProduct ? (
                <ProductsDropdown
                  label='Seçili Ürünler'
                  multiple
                  value={coupon.productIDs}
                  onChange={(newIDs) =>
                    setCoupon({ ...coupon, productIDs: newIDs })
                  }
                />
              ) : (
                <TextField
                  label='Seçili Ürünler'
                  size='small'
                  value='Tüm ürünler'
                  disabled
                  fullWidth
                />
              )}

              <Switch
                checked={coupon.specificForProduct}
                onChange={() =>
                  setCoupon({
                    ...coupon,
                    specificForCategory: false,
                    specificForProduct: !coupon.specificForProduct,
                    categoryIDs: [],
                    productIDs: [],
                  })
                }
              />
            </Box>
          </Paper>

          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 3,
              flexGrow: 1,
              minWidth,
              maxWidth,
            }}
          >
            <Typography
              sx={{
                ...sharedStyles.h5,
                fontWeight: 'bold',
              }}
            >
              Kullanım Limitleri
            </Typography>
            <Divider />

            <ToggledNumberInput
              label='Toplam Kullanım Limiti'
              value={coupon.usageLimitPerCoupon}
              defaultValue={100}
              onChange={(newValue) =>
                setCoupon({ ...coupon, usageLimitPerCoupon: newValue })
              }
            />

            <ToggledNumberInput
              label='Kullanıcı Başı Kullanım Limiti'
              value={coupon.usageLimitPerCustomer}
              defaultValue={1}
              onChange={(newValue) =>
                setCoupon({ ...coupon, usageLimitPerCustomer: newValue })
              }
            />
          </Paper>
        </Box>

        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText }}
        >
          Kaydet
        </Button>
      </Box>
    </Box>
  );
};
